import { Instagram, Mail, Phone, MapPin, Heart, Shield, Play } from "lucide-react";
import { LotusIcon } from "@/components/ui/lotus-icon";

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-gradient-to-b from-primary to-primary-glow text-gold-light overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23c4a96a' fill-opacity='0.1'%3E%3Cpath d='M30 30c0-11.046-8.954-20-20-20s-20 8.954-20 20 8.954 20 20 20 20-8.954 20-20zm0 0c0 11.046 8.954 20 20 20s20-8.954 20-20-8.954-20-20-20-20 8.954-20 20z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="container mx-auto px-4 py-16 relative z-10">
        <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="mb-6">
              <h3 className="font-serif text-3xl font-bold text-gold text-glow mb-2">
                LUNSSENCE
              </h3>
              <p className="text-gold-light text-lg">Herbal Tea</p>
              
              {/* Logo/Icon */}
              <LotusIcon size={60} className="mt-4 text-gold fill-current" />
            </div>
            
            <p className="text-gold-light/80 leading-relaxed mb-6 max-w-md">
              Ontdek de eeuwenoude kracht van blauwe lotus. Premium kruidenthee voor 
              diepe ontspanning, hormoonbalans en bewuste rituelen.
            </p>
            
            <div className="flex gap-4">
              <a
                href="https://www.instagram.com/lunssence_herbal_tea?igsh=MWptbG85aGd3eXFvcQ=="
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center hover:bg-gold/30 hover:scale-110 transition-all duration-300 group"
              >
                <Instagram className="w-6 h-6 text-gold group-hover:text-gold-light" />
              </a>
              <a
                href="https://www.tiktok.com/@lunssence.herbal?_r=1&_t=ZG-9192I7ELuxV"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center hover:bg-gold/30 hover:scale-110 transition-all duration-300 group"
              >
                <Play className="w-6 h-6 text-gold group-hover:text-gold-light" />
              </a>
              <a
                href="mailto:info@lunssence.com"
                className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center hover:bg-gold/30 hover:scale-110 transition-all duration-300 group"
              >
                <Mail className="w-6 h-6 text-gold group-hover:text-gold-light" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-serif text-xl font-bold text-gold mb-6">
              Navigatie
            </h4>
            <nav className="space-y-3">
              <a
                href="/#mission"
                className="block text-gold-light/80 hover:text-gold transition-colors duration-200 hover:translate-x-1 transform"
              >
                Over Ons
              </a>
              <a
                href="/producten"
                className="block text-gold-light/80 hover:text-gold transition-colors duration-200 hover:translate-x-1 transform"
              >
                Producten
              </a>
              <a
                href="/#reviews"
                className="block text-gold-light/80 hover:text-gold transition-colors duration-200 hover:translate-x-1 transform"
              >
                Reviews
              </a>
              <a
                href="/#faq"
                className="block text-gold-light/80 hover:text-gold transition-colors duration-200 hover:translate-x-1 transform"
              >
                FAQ
              </a>
              <a
                href="mailto:info@lunssence.com"
                className="block text-gold-light/80 hover:text-gold transition-colors duration-200 hover:translate-x-1 transform"
              >
                Contact
              </a>
            </nav>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-serif text-xl font-bold text-gold mb-6">
              Contact
            </h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-gold mt-1 flex-shrink-0" />
                <div>
                  <p className="text-gold-light/80">E-mail</p>
                  <a href="mailto:info@lunssence.com" className="text-gold hover:text-gold-light transition-colors">
                    info@lunssence.com
                  </a>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-gold mt-1 flex-shrink-0" />
                <div>
                  <p className="text-gold-light/80">WhatsApp</p>
                  <a href="https://wa.me/31619471444" className="text-gold hover:text-gold-light transition-colors">
                    +31 6 1947 1444
                  </a>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-gold mt-1 flex-shrink-0" />
                <div>
                  <p className="text-gold-light/80">Adres</p>
                  <p className="text-gold">
                    Amsterdam, Nederland
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* NVWA Registration Badge */}
        <div className="border-t border-gold/20 mt-12 pt-8 pb-8">
          <div className="flex items-center justify-center gap-3 text-gold/90">
            <Shield className="w-5 h-5 text-gold" />
            <p className="text-sm font-medium">
              Lunssence is geregistreerd bij de Nederlandse Voedsel- en Warenautoriteit (NVWA)
            </p>
          </div>
        </div>

        {/* Payment Icons */}
        <div className="border-t border-gold/20 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-6">
              <p className="text-gold/80 text-sm">Veilig betalen met:</p>
              <div className="flex items-center gap-4">
                <div className="bg-white rounded px-3 py-2 text-sm font-semibold text-primary">iDEAL</div>
                <div className="bg-white rounded px-3 py-2 text-sm font-semibold text-primary">PayPal</div>
                <div className="bg-white rounded px-3 py-2 text-sm font-semibold text-primary">Klarna</div>
              </div>
            </div>
            
            <div className="flex items-center gap-4 text-sm text-gold/80">
              <a href="/privacy" className="hover:text-gold transition-colors">Privacy</a>
              <span>•</span>
              <a href="/algemene-voorwaarden" className="hover:text-gold transition-colors">Voorwaarden</a>
              <span>•</span>
              <a href="/retour" className="hover:text-gold transition-colors">Retour</a>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-gold/20 mt-8 pt-8 text-center">
          <p className="text-gold/90 text-sm mb-2">
            © {currentYear} Lunssence. Alle rechten voorbehouden.
          </p>
          <div className="flex items-center justify-center gap-3">
            <p className="text-gold/90 text-xs flex items-center gap-1">
              Gemaakt met <Heart className="w-3 h-3 text-destructive fill-current" /> in Nederland
            </p>
            
            {/* Subtiele admin login button - alleen zichtbaar op hover */}
            <button
              onClick={() => window.location.href = '/admin/login'}
              className="opacity-20 hover:opacity-100 transition-all duration-300 text-gold/40 hover:text-gold hover:scale-110"
              title="Admin toegang"
              aria-label="Admin login"
            >
              <Shield className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};